import { MeasurementResult } from '../domain/sizing';

/**
 * Minimal interface for what getNavigationNodeId needs from a sizing service.
 * Avoids coupling this utility to the concrete SizingStateService class.
 */
interface SizingServiceLike {
    getPlacement(keypointId: string): { nodeId: number } | undefined;
}

/**
 * Resolve the navigation node ID for a measurement based on its context type.
 * - Diameters/offsets/min-max/custom-diameter: the measurement's nodeId
 * - Lengths/custom-lengths: fromNodeId (first point)
 * - Angles: vertex keypoint (middle of the triplet) resolved via placement
 * - Custom angles: the vertex node ID (middle of the triplet)
 * - Derived: no navigation
 */
export function getNavigationNodeId(
    measurement: MeasurementResult,
    sizingService: SizingServiceLike
): number | null {
    // navigationNodeId already covers most cases
    if (measurement.navigationNodeId != null) {
        return measurement.navigationNodeId;
    }

    // For angles, resolve the vertex keypoint (index 1) to a node
    if (measurement.context.type === 'angle') {
        const vertexKeypointId = measurement.context.keypointIds[1];
        const placement = sizingService.getPlacement(vertexKeypointId);
        return placement?.nodeId ?? null;
    }

    // For custom angles, the vertex is the middle node ID
    if (measurement.context.type === 'custom-angle') {
        return measurement.context.nodeIds[1] ?? null;
    }

    return null;
}
